import { OrbindApiError } from '@orbind/api-client'
import type { StatusTone } from '@orbind/tokens'
import { Alert, Badge, Button, ConfirmDialog, EmptyState, LoadingState, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ProgressBar, Timeline, formatDate, formatDateTime, progressLabel, type TimelineEvent } from '../components/detail.tsx'
import { SlaBadge } from '../components/badges.tsx'
import { api } from '../lib/api.ts'
import { useBootstrap } from '../lib/catalog.ts'
import { useT } from '../lib/i18n.tsx'
import { LEVEL_ICON, LEVEL_ORDER, PortfolioExplorer, useCatalogLabel } from './PortfolioExplorer.tsx'
import { PortfolioNodeForm } from './PortfolioNodeForm.tsx'
import './portfolio-node.css'

/**
 * Detalhe do item do portfólio (região `showPort` do protótipo v2).
 *
 * Tela real: `GET /portfolio/:id` devolve o nó, os filhos já com progresso e
 * contagem de netos, o caminho até a raiz e os agregados da subárvore. A árvore
 * fica no explorer do shell, ao lado — não em uma rota separada.
 */

type Tab = 'overview' | 'structure' | 'items' | 'activity'

export function PortfolioNodePage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const t = useT()
  const toast = useToast()
  const queryClient = useQueryClient()
  const bootstrap = useBootstrap()
  const label = useCatalogLabel()
  const [tab, setTab] = useState<Tab>('overview')
  /**
   * O "+" do explorer é do MÓDULO: ele cria uma Solução na raiz, como diz o
   * `createLabel` de `modules.ts`. "Adicionar filho" no cabeçalho é outra coisa
   * — cria o próximo nível abaixo do nó aberto.
   */
  const [criando, setCriando] = useState<'child' | 'solution' | null>(null)
  const [editando, setEditando] = useState(false)
  const [arquivando, setArquivando] = useState(false)

  const detail = useQuery({
    queryKey: ['portfolio', id],
    queryFn: async () => (await api.portfolio[':id'].$get({ param: { id } })).json(),
  })

  // As duas abas de listagem reusam as rotas que já existem, filtradas pelo nó.
  const workItems = useQuery({
    queryKey: ['work-items', { portfolioNodeId: id }],
    queryFn: async () =>
      (await api['work-items'].$get({ query: { portfolioNodeId: id, limit: 100 } })).json(),
    enabled: id !== '',
  })

  const levelLabel = (level: string) => {
    const key = `portfolio_level.${level.toLowerCase()}`
    const value = t(key)
    return value === key ? level : value
  }

  const arquivar = useMutation({
    mutationFn: async () => (await api.portfolio[':id'].archive.$post({ param: { id } })).json(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['portfolio'] })
      toast.push({
        tone: 'success',
        message:
          data.archived > 1
            ? `Item arquivado junto com ${data.archived - 1} descendente(s).`
            : 'Item arquivado.',
      })
      setArquivando(false)
      navigate('/portfolio/tree')
    },
    onError: (error) => {
      setArquivando(false)
      toast.push({
        tone: 'danger',
        message: error instanceof OrbindApiError ? error.message : 'Não foi possível arquivar.',
      })
    },
  })

  /**
   * Atividade do nó.
   *
   * Não existe tabela de histórico do portfólio: a timeline é derivada dos
   * instantes reais que o nó e o que pende dele já registram — criação, última
   * alteração, demandas que chegaram e itens concluídos. Nada é inventado.
   */
  const activity = useMemo<TimelineEvent[]>(() => {
    if (!detail.data) return []
    const { node } = detail.data
    const pessoas = new Map((bootstrap.data?.members ?? []).map((p) => [p.id, p.name]))
    const eventos: TimelineEvent[] = [
      {
        id: `created-${node.id}`,
        text: (
          <>
            <strong>{node.name}</strong> foi criado no portfólio.
          </>
        ),
        meta: `${formatDateTime(node.createdAt)}${node.createdBy ? ` · ${pessoas.get(node.createdBy) ?? 'Sistema'}` : ''}`,
        tone: 'muted',
        at: new Date(node.createdAt).getTime(),
      },
    ]
    if (node.updatedAt && node.updatedAt !== node.createdAt) {
      eventos.push({
        id: `updated-${node.id}`,
        text: 'Cadastro atualizado.',
        meta: `${formatDateTime(node.updatedAt)}${node.updatedBy ? ` · ${pessoas.get(node.updatedBy) ?? 'Sistema'}` : ''}`,
        at: new Date(node.updatedAt).getTime(),
      })
    }
    for (const item of workItems.data?.items ?? []) {
      if (!item.completedAt) continue
      eventos.push({
        id: `wi-${item.id}`,
        text: (
          <>
            Item de Trabalho <strong>{item.code}</strong> concluído — {item.title}
          </>
        ),
        meta: `${formatDateTime(item.completedAt)}${item.assignee?.name ? ` · ${item.assignee.name}` : ''}`,
        tone: 'success',
        at: new Date(item.completedAt).getTime(),
      })
    }
    return eventos.sort((a, b) => b.at - a.at)
  }, [detail.data, workItems.data, bootstrap.data])

  const nextLevel = detail.data
    ? LEVEL_ORDER[LEVEL_ORDER.indexOf(detail.data.node.level as (typeof LEVEL_ORDER)[number]) + 1]
    : undefined

  return (
    <>
      <PortfolioExplorer selectedId={id} onCreate={() => setCriando('solution')} />

      {detail.isPending ? (
        <LoadingState variant="detail" />
      ) : !detail.data ? (
        <div style={{ padding: 28 }}>
          <Alert tone="danger" title="Não foi possível carregar">
            Verifique o link ou tente novamente.
          </Alert>
        </div>
      ) : (
        (() => {
          const { node, children, breadcrumb, counts, progress } = detail.data
          const status = bootstrap.data?.portfolioStatuses?.find((s) => s.id === node.portfolioStatusId)
          const owner = bootstrap.data?.members.find((c) => c.id === node.ownerMemberId)
          const area = bootstrap.data?.areas.find((a) => a.id === node.companyAreaId)
          const parent = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1] : null

          const abas: readonly { id: Tab; label: string; count?: number }[] = [
            { id: 'overview', label: 'Visão geral' },
            { id: 'structure', label: 'Estrutura', count: children.length },
            { id: 'items', label: 'Itens de Trabalho', count: counts.workItems },
            { id: 'activity', label: 'Atividade', count: activity.length },
          ]

          return (
            <div className="pt-detail">
              <div className="pt-head">
                <div style={{ minWidth: 280, flex: '1 1 380px' }}>
                  <div className="pt-head__meta">
                    <span className="pt-head__icon">
                      <i className={LEVEL_ICON[node.level] ?? 'fas fa-cube'} aria-hidden />
                    </span>
                    <span className="pt-head__code">{node.code}</span>
                    <span className="pt-pill pt-pill--level">{levelLabel(node.level)}</span>
                    {status ? (
                      <Badge tone={status.tone as StatusTone}>{label(status.nameTranslationKey, status.code)}</Badge>
                    ) : null}
                  </div>
                  <h1 className="pt-head__title">{node.name}</h1>
                  <p className="pt-head__path">
                    {breadcrumb.length === 0
                      ? 'Raiz do portfólio'
                      : breadcrumb.map((b) => b?.name).filter(Boolean).join(' › ')}
                  </p>
                </div>

                <div className="pt-head__actions">
                  {nextLevel ? (
                    <Button icon="fas fa-plus" onClick={() => setCriando('child')}>
                      Adicionar {levelLabel(nextLevel).toLowerCase()}
                    </Button>
                  ) : null}
                  <Button icon="fas fa-pen" onClick={() => setEditando(true)}>
                    Editar
                  </Button>
                  <Button variant="danger" icon="fas fa-archive" onClick={() => setArquivando(true)}>
                    Arquivar
                  </Button>
                </div>
              </div>

              {/* Faixa de progresso — protótipo `showPort.html:22-27`. */}
              <div className="pt-progress">
                <span className="pt-progress__label">Progresso</span>
                <ProgressBar ratio={progress.ratio} />
                <span className="pt-progress__value">{progressLabel(progress)}</span>
                <span className="pt-progress__owner">
                  {progress.total > 0 ? `${progress.done} de ${progress.total} itens · ` : ''}
                  Responsável: {owner?.name ?? 'não definido'}
                </span>
              </div>

              <div className="pt-card">
                <div className="pt-tabs" role="tablist" aria-label="Seções do item do portfólio">
                  {abas.map((aba) => (
                    <button
                      key={aba.id}
                      type="button"
                      role="tab"
                      aria-selected={tab === aba.id}
                      className={`pt-tab${tab === aba.id ? ' pt-tab--on' : ''}`}
                      onClick={() => setTab(aba.id)}
                    >
                      {aba.label}
                      {aba.count === undefined ? null : <span className="pt-tab__count">{aba.count}</span>}
                    </button>
                  ))}
                </div>

                {tab === 'overview' ? (
                  <div className="pt-body">
                    <div>
                      <h3 className="pt-section">Descrição</h3>
                      <p className="pt-desc">
                        {node.description?.trim() ? node.description : 'Sem descrição cadastrada.'}
                      </p>
                    </div>

                    {/* Propriedades do protótipo (`_shell.html:3105-3110`). Nível e
                        situação não se repetem aqui: já estão nas pills do topo. */}
                    <div className="pt-props">
                      {[
                        { label: 'Responsável', value: owner?.name ?? 'Não definido' },
                        { label: 'Área', value: area ? label(area.nameTranslationKey, area.code) : 'Não definida' },
                        { label: 'Código', value: node.code },
                        {
                          label: 'Pai',
                          value: parent ? `${parent.name} (${levelLabel(parent.level)})` : 'Raiz do portfólio',
                        },
                      ].map((prop) => (
                        <div key={prop.label} className="pt-prop">
                          <span className="pt-prop__label">{prop.label}</span>
                          <span className="pt-prop__value">{prop.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {tab === 'structure' ? (
                  <div className="pt-body">
                    {children.length === 0 ? (
                      <EmptyState
                        icon="fas fa-layer-group"
                        title="Este nó ainda não tem filhos"
                        description={
                          nextLevel
                            ? `Crie o primeiro ${levelLabel(nextLevel).toLowerCase()} para detalhar este item.`
                            : 'Ação é o último nível da hierarquia: não há filhos abaixo dela.'
                        }
                        action={
                          nextLevel ? (
                            <Button variant="primary" icon="fas fa-plus" onClick={() => setCriando('child')}>
                              Adicionar {levelLabel(nextLevel).toLowerCase()}
                            </Button>
                          ) : undefined
                        }
                      />
                    ) : (
                      <div className="pt-children">
                        {children.map((child) => {
                          const childStatus = bootstrap.data?.portfolioStatuses?.find(
                            (s) => s.id === child.portfolioStatusId,
                          )
                          return (
                            <button
                              key={child.id}
                              type="button"
                              className="pt-child"
                              onClick={() => navigate(`/portfolio/${child.id}`)}
                            >
                              <span className="pt-child__icon">
                                <i className={LEVEL_ICON[child.level] ?? 'fas fa-cube'} aria-hidden />
                              </span>
                              <span className="pt-child__main">
                                <span className="pt-child__name">{child.name}</span>
                                <span className="pt-child__meta">
                                  {levelLabel(child.level)} ·{' '}
                                  {child.childCount === 0
                                    ? 'sem filhos'
                                    : `${child.childCount} ${child.childCount === 1 ? 'filho' : 'filhos'}`}{' '}
                                  · {progressLabel(child.progress)}
                                </span>
                              </span>
                              <ProgressBar ratio={child.progress.ratio} small />
                              {childStatus ? (
                                <Badge tone={childStatus.tone as StatusTone}>
                                  {label(childStatus.nameTranslationKey, childStatus.code)}
                                </Badge>
                              ) : (
                                <Badge tone="neutral">Sem situação</Badge>
                              )}
                              <i
                                className="fas fa-chevron-right"
                                aria-hidden
                                style={{ fontSize: 11, color: 'var(--ob-text-muted)' }}
                              />
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                ) : null}

                {tab === 'items' ? (
                  <div className="pt-body">
                    {workItems.isPending ? (
                      <LoadingState rows={4} />
                    ) : workItems.isError ? (
                      <Alert tone="danger" title="Não foi possível carregar os Itens de Trabalho">
                        Tente novamente em instantes.
                      </Alert>
                    ) : (workItems.data?.items.length ?? 0) === 0 ? (
                      <EmptyState
                        icon="fas fa-tasks"
                        title="Ainda não há Itens de Trabalho neste contexto"
                        description="Converta uma demanda ou crie um item apontando este nó do portfólio."
                      />
                    ) : (
                      <div className="pt-rows">
                        {workItems.data?.items.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            className="pt-row"
                            onClick={() => navigate(`/work-items/${item.id}`)}
                          >
                            <span className="pt-child__icon" aria-hidden>
                              <i className={item.type.icon ?? 'fas fa-tasks'} />
                            </span>
                            <span className="pt-row__code">{item.code}</span>
                            <span className="pt-row__title">{item.title}</span>
                            <Badge tone={item.status.tone as StatusTone}>
                              {label(
                                `work_item_status.${item.status.code.toLowerCase()}.name`,
                                item.status.code,
                              )}
                            </Badge>
                            {item.assignee?.name ? (
                              <span className="pt-row__code">{item.assignee.name}</span>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}

                {tab === 'activity' ? (
                  <div className="pt-body">
                    {activity.length === 0 ? (
                      <EmptyState
                        icon="fas fa-history"
                        title="Sem atividade registrada"
                        description="Criações, demandas recebidas e Itens de Trabalho concluídos aparecem aqui."
                      />
                    ) : (
                      <Timeline events={activity} />
                    )}
                  </div>
                ) : null}
              </div>

              <PortfolioNodeForm
                open={criando === 'child' && nextLevel !== undefined}
                onClose={() => setCriando(null)}
                mode="create"
                level={nextLevel}
                levelLabel={nextLevel ? levelLabel(nextLevel) : ''}
                parentId={node.id}
                parentName={node.name}
              />
              <PortfolioNodeForm
                open={criando === 'solution'}
                onClose={() => setCriando(null)}
                mode="create"
                level="SOLUTION"
                levelLabel={levelLabel('SOLUTION')}
                parentId={null}
              />
              <PortfolioNodeForm
                open={editando}
                onClose={() => setEditando(false)}
                mode="edit"
                nodeId={node.id}
                levelLabel={levelLabel(node.level)}
                initial={{
                  code: node.code,
                  name: node.name,
                  description: node.description ?? '',
                  portfolioStatusId: node.portfolioStatusId,
                  ownerMemberId: node.ownerMemberId,
                  companyAreaId: node.companyAreaId,
                }}
              />
              <ConfirmDialog
                open={arquivando}
                destructive
                loading={arquivar.isPending}
                title={`Arquivar ${node.name}?`}
                confirmLabel="Arquivar"
                description={
                  <>
                    O item sai do portfólio ativo{' '}
                    {detail.data.subtree.descendants > 0
                      ? `junto com ${detail.data.subtree.descendants} descendente(s)`
                      : 'sem descendentes'}
                    . Nada é excluído: demandas e Itens de Trabalho já vinculados continuam existindo e o histórico é
                    preservado. Criado em {formatDate(node.createdAt)}.
                  </>
                }
                onCancel={() => setArquivando(false)}
                onConfirm={() => arquivar.mutate()}
              />
            </div>
          )
        })()
      )}
    </>
  )
}
